import { FC, useEffect, useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import {
  Avatar,
  Box,
  BoxProps,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Container,
  Fade,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Show,
  Text,
} from "@chakra-ui/react";
import {
  ChevronRightIcon,
  ExternalLinkIcon,
  HamburgerIcon,
  SettingsIcon,
} from "@chakra-ui/icons";
import { Logo } from "@/components/Logo";
import { MobileMenu } from "@/components/MobileMenu";
import { ConfirmModal } from "@/components/ConfirmModal";
import { LanguagePicker } from "@/components/LanguagePicker";
import { NO_DATA_PLACEHOLDER } from "@/constants/common";
import { ROUTES } from "@/constants/routes";
import { useAuth } from "@/providers/UserProvider";
import { Locale, HomeContent, LandingPageContent } from "@/constants/landingPages";

interface IBreadcrumb {
  href?: string;
  label: string;
}

interface Props extends BoxProps {
  breadcrumbs?: IBreadcrumb[];
  areBreadcrumbsLoading?: boolean;
  isFullWidth?: boolean;
  isWithBreadcrumbs?: boolean;
  locale?: Locale;
  content?: HomeContent | LandingPageContent;
}

export const Header: FC<Props> = ({
  breadcrumbs,
  areBreadcrumbsLoading = false,
  isFullWidth = false,
  isWithBreadcrumbs = true,
  locale,
  content,
  ...props
}) => {
  const { asPath, pathname } = useRouter();
  const { userData, signOut } = useAuth();

  const [isPageScrolled, setIsPageScrolled] = useState(false);
  const [isLogOutModalOpened, setIsLogOutModalOpened] = useState(false);

  // Build home route with locale if available
  const homeRoute = locale ? `/${locale}` : ROUTES.HOME;
  const faqRoute = locale ? `/${locale}/faq` : "/sk/faq";
  const homeLabel = content?.nav?.home || "Domov";
  const faqLabel = "FAQ"; // FAQ is universal across all languages

  useEffect(() => {
    const handleScroll = () => {
      setIsPageScrolled(window.scrollY > 0);
    };
    handleScroll();

    document.addEventListener("scroll", handleScroll);

    return () => {
      document.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <Box
      as="header"
      position="sticky"
      top="0"
      right="0"
      left="0"
      py="3"
      bg="gray.300"
      zIndex={40}
      boxShadow={isPageScrolled ? "md" : undefined}
      {...props}
    >
      <Container maxW={isFullWidth ? "100%" : "7xl"}>
        <Flex alignItems="center">
          <Link href={homeRoute}>
            <Logo />
          </Link>
          <Box ml="auto">
            <Flex alignItems="center" gap="8">
              {/* Language Picker - show only on landing pages */}
              {locale && (
                <LanguagePicker currentLocale={locale} />
              )}
              <Show above="md">
                <Flex gap="6">
                  <Button
                    as={Link}
                    variant="transparent"
                    href={homeRoute}
                    color={pathname === homeRoute ? "primary" : "gray.500"}
                  >
                    {homeLabel}
                  </Button>
                  {locale && (
                    <Button
                      as={Link}
                      variant="transparent"
                      href={faqRoute}
                      color={pathname === faqRoute ? "primary" : "gray.500"}
                    >
                      {faqLabel}
                    </Button>
                  )}
                  {!userData && (
                    <Button
                      as={Link}
                      variant="transparent"
                      href={ROUTES.SIGN_IN()}
                      color={
                        pathname === ROUTES.SIGN_IN() ? "primary" : "gray.500"
                      }
                    >
                      Prihlásiť sa
                    </Button>
                  )}
                </Flex>
              </Show>
              {userData && (
                <Show above="md">
                  <Menu>
                    <Show above="md">
                      <MenuButton
                        w="160px"
                        as={Button}
                        variant="secondary"
                        leftIcon={<SettingsIcon />}
                      >
                        Administrácia
                      </MenuButton>
                    </Show>
                    <Show below="md">
                      <MenuButton
                        as={IconButton}
                        icon={<HamburgerIcon />}
                        variant="outline"
                        aria-label="Options"
                      />
                    </Show>
                    <MenuList>
                      <Box
                        p="4"
                        borderBottom="1px solid"
                        borderColor="gray.200"
                      >
                        <Text textStyle="base.250" fontWeight="400">
                          Prihlásený ako
                        </Text>
                        <Text textStyle="base.250" fontWeight="600" mt="2">
                          {userData?.email ?? NO_DATA_PLACEHOLDER}
                        </Text>
                      </Box>
                      <MenuItem
                        as={Link}
                        href={ROUTES.DASHBOARD_CALENDAR()}
                        textStyle="base.250"
                        py="3"
                      >
                        Kalendár
                      </MenuItem>
                      <MenuItem
                        as={Link}
                        href={ROUTES.DASHBOARD_NO_SHOW()}
                        textStyle="base.250"
                        py="3"
                      >
                        Zrušenia a no-show
                      </MenuItem>
                      {/* <MenuItem
                        as={Link}
                        href={ROUTES.DASHBOARD_STATS()}
                        textStyle="base.250"
                        py="3"
                      >
                        Štatistiky
                      </MenuItem> */}
                      <MenuItem
                        as={Link}
                        href={ROUTES.PROFILE_MANAGER()}
                        textStyle="base.250"
                        py="3"
                      >
                        Nastavenia
                      </MenuItem>
                      <MenuItem
                        as={Link}
                        href={ROUTES.PUBLIC_SERVICE_RESERVATION_PAGE({
                          id: userData.id
                        })}
                        target="_blank"
                        textStyle="base.250"
                        py="3"
                        icon={<ExternalLinkIcon />}
                      >
                        Môj rezervačný formulár
                      </MenuItem>
                      <MenuItem
                        textStyle="base.250"
                        py="3"
                        onClick={() => {
                          setIsLogOutModalOpened(true);
                        }}
                        color="error"
                      >
                        Odhlásiť sa
                      </MenuItem>
                    </MenuList>
                  </Menu>
                </Show>
              )}
              <Show below="md">
                <MobileMenu>
                  <Flex
                    flexDir="column"
                    mt="10"
                    mb="3"
                    h="-webkit-fill-available"
                  >
                    <Flex
                      pb="4"
                      flexDir="column"
                      borderBottom={userData ? "1px solid" : undefined}
                      borderColor="gray.800"
                      alignItems="center"
                    >
                      {userData && (
                        <>
                          <Avatar
                            name={userData.email || ""}
                            src={undefined}
                            size="lg"
                          />
                          <Text textStyle="base.500" fontWeight="600" mt="3">
                            {userData?.email ?? NO_DATA_PLACEHOLDER}
                          </Text>
                        </>
                      )}
                    </Flex>
                    <Flex
                      flexDir="column"
                      alignItems="center"
                      mt="4"
                      pb={userData ? "4" : "0"}
                      borderBottom={userData ? "1px solid" : undefined}
                      borderColor="gray.800"
                      gap="4"
                    >
                      {!userData && (
                        <Button
                          as={Link}
                          fontSize="22"
                          variant="transparent"
                          href={ROUTES.SIGN_IN()}
                          rightIcon={
                            <ChevronRightIcon boxSize="6" color="gray.500" />
                          }
                          fontWeight="medium"
                        >
                          Prihlásiť sa
                        </Button>
                      )}
                      {userData && (
                        <>
                          <Button
                            as={Link}
                            fontSize="22"
                            variant="transparent"
                            href={ROUTES.PROFILE_MANAGER()}
                            rightIcon={
                              <ChevronRightIcon boxSize="6" color="gray.500" />
                            }
                          >
                            Nastavenia
                          </Button>
                          <Button
                            as={Link}
                            fontSize="22"
                            variant="transparent"
                            href={ROUTES.DASHBOARD_CALENDAR()}
                            rightIcon={
                              <ChevronRightIcon boxSize="6" color="gray.500" />
                            }
                          >
                            Kalendár
                          </Button>
                          <Button
                            as={Link}
                            fontSize="22"
                            variant="transparent"
                            href={ROUTES.DASHBOARD_NO_SHOW()}
                            rightIcon={
                              <ChevronRightIcon boxSize="6" color="gray.500" />
                            }
                          >
                            Zrušenia a no-show
                          </Button>
                          {/* <Button
                            as={Link}
                            fontSize="22"
                            variant="transparent"
                            href={ROUTES.DASHBOARD_STATS()}
                            rightIcon={
                              <ChevronRightIcon boxSize="6" color="gray.500" />
                            }
                          >
                            Štatistiky
                          </Button> */}
                          <Button
                            as={Link}
                            href={ROUTES.PUBLIC_SERVICE_RESERVATION_PAGE({
                              id: userData.id
                            })}
                            fontSize="22"
                            variant="transparent"
                            target="_blank"
                            rightIcon={<ExternalLinkIcon />}
                          >
                            Môj rezervačný formulár
                          </Button>
                        </>
                      )}
                      <Button
                        as={Link}
                        fontSize="22"
                        variant="transparent"
                        href={homeRoute}
                        rightIcon={
                          <ChevronRightIcon boxSize="6" color="gray.500" />
                        }
                      >
                        {homeLabel}
                      </Button>
                      {locale && (
                        <Button
                          as={Link}
                          fontSize="22"
                          variant="transparent"
                          href={faqRoute}
                          rightIcon={
                            <ChevronRightIcon boxSize="6" color="gray.500" />
                          }
                        >
                          {faqLabel}
                        </Button>
                      )}
                      {userData && (
                        <Button
                          variant="transparent"
                          color="error"
                          fontWeight="semibold"
                          onClick={() => {
                            setIsLogOutModalOpened(true);
                          }}
                          mt="auto"
                          mb="6"
                          fontSize="22"
                        >
                          Odhlásiť sa
                        </Button>
                      )}
                    </Flex>
                  </Flex>
                </MobileMenu>
              </Show>
            </Flex>
          </Box>
        </Flex>
        {isWithBreadcrumbs && (
          <Box>
            <Fade in={!areBreadcrumbsLoading}>
              <Breadcrumb
                display={{ base: "none", md: "block" }}
                spacing="8px"
                separator={<ChevronRightIcon color="gray.500" />}
                listProps={{
                  flexDirection: ["column", "row"],
                  alignItems: "flex-start",
                }}
              >
                {breadcrumbs?.map(({ href, label }) => {
                  if (asPath === href) {
                    return (
                      <BreadcrumbItem key={label} color="gray.500">
                        <Text color="gray.500">{label}</Text>
                      </BreadcrumbItem>
                    );
                  }
                  return (
                    <BreadcrumbItem key={label} isCurrentPage={asPath === href}>
                      {href ? (
                        <Link href={href} color="gray.500">
                          {label}
                        </Link>
                      ) : (
                        <Text color="gray.500">{label}</Text>
                      )}
                    </BreadcrumbItem>
                  );
                })}
              </Breadcrumb>
            </Fade>
          </Box>
        )}
      </Container>
      {isLogOutModalOpened && (
        <ConfirmModal
          title="Naozaj sa chcete odhlasiť?"
          submitButtonText="Odhlásiť"
          onSubmit={() => {
            signOut(true);
          }}
          onClose={() => {
            setIsLogOutModalOpened(false);
          }}
        />
      )}
    </Box>
  );
};
